import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionMonitoringPage } from '@/features/transaction-monitoring/TransactionMonitoringPage';
import { ScreeningDetailPage } from '@/features/transaction-monitoring/ScreeningDetailPage';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { MonitoringPage } from '@/pages/MonitoringPage';
import { ClientListPage } from '@/pages/ClientListPage';
import { ClientDetailPage } from '@/pages/ClientDetailPage';
import { TransactionHistoryPage } from '@/pages/TransactionHistoryPage';
import { PricingPage } from '@/pages/PricingPage';
import { RevenuePage } from '@/pages/RevenuePage';
import { IncomingPage } from '@/pages/IncomingPage';
import { ConveraLogsPage } from '@/pages/ConveraLogsPage';
import { NewOperationPage } from '@/pages/NewOperationPage';
import { OperatorListPage } from '@/pages/OperatorListPage';
import { OperatorCreatePage } from '@/pages/OperatorCreatePage';
import { OperatorDetailPage } from '@/pages/OperatorDetailPage';
import { CREATE_OPERATOR, OPERATOR_SCREEN_PERMISSIONS } from '@/features/operators/types';
import { Layout, ProtectedRoute } from '@/components/layout';
import { RequirePermission, RoleGate } from '@/components/auth';
import { InactivityWarningModal } from '@/components/InactivityWarningModal';
import { useAuthStore } from '@/store/authStore';
import { useMonitoringStore } from '@/store/monitoringStore';
import { useReviewModalStore } from '@/store/reviewModalStore';
import { clearLookupCache } from '@/hooks/useLookups';
import { registerNavigate } from '@/lib/api-client';

const USER_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/** Registers React Router's navigate with the API client and sets up inactivity listeners. */
export function AppShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = token !== null;
  const resetInactivityTimer = useAuthStore((s) => s.resetInactivityTimer);

  // Register navigate so the API client 401 interceptor can redirect
  useEffect(() => {
    registerNavigate(navigate);
  }, [navigate]);

  // Tenant-switch cache invalidation: clear the entire React Query cache and
  // reset every Zustand slice that holds tenant-scoped data so the UI refetches
  // from the new tenant's webapp backend on the next render. The auth store
  // and layout preferences (sidebar open/closed) are intentionally NOT reset.
  useEffect(() => {
    const handler = () => {
      queryClient.clear();
      useMonitoringStore.getState().resetTenantScopedState();
      useReviewModalStore.getState().resetTenantScopedState();
      clearLookupCache();
    };
    window.addEventListener('cbs:tenant-switched', handler);
    return () => window.removeEventListener('cbs:tenant-switched', handler);
  }, [queryClient]);

  // Global inactivity timer reset on trusted user interaction. Avoid raw
  // `scroll`: auto-refreshing pages can re-render and trigger browser scroll
  // anchoring without an operator actually doing anything.
  useEffect(() => {
    if (!isAuthenticated) return;

    const resetTimer = (event: Event) => {
      if (event.isTrusted) {
        resetInactivityTimer();
      }
    };

    USER_ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, resetTimer, true);
    });

    return () => {
      USER_ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, resetTimer, true);
      });
    };
  }, [isAuthenticated, resetInactivityTimer]);

  // Authenticated routes return to the dashboard when a permission check fails;
  // unauthenticated users are caught upstream by ProtectedRoute.
  const denied = <Navigate to="/" replace />;

  return (
    <Routes>
      {/* Public route */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes — wrapped by Layout shell */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
            <InactivityWarningModal />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route
          path="/transaction-monitoring"
          element={
            <RequirePermission any={['COMPLIANCE_VIEW']} fallback={denied}>
              <RoleGate role="cbs_admin" fallback={denied}>
                <TransactionMonitoringPage />
              </RoleGate>
            </RequirePermission>
          }
        />
        <Route
          path="/transaction-monitoring/:screeningId"
          element={
            <RequirePermission any={['COMPLIANCE_VIEW']} fallback={denied}>
              <RoleGate role="cbs_admin" fallback={denied}>
                <ScreeningDetailPage />
              </RoleGate>
            </RequirePermission>
          }
        />
        <Route
          path="/monitoring"
          element={
            <RequirePermission any={['APPROVE_PAYMENT']} fallback={denied}>
              <MonitoringPage />
            </RequirePermission>
          }
        />
        <Route
          path="/clients"
          element={
            <RequirePermission any={['MANAGE_CLIENTS', 'CREATE_CLIENT']} fallback={denied}>
              <ClientListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/clients/:clientId"
          element={
            <RequirePermission any={['MANAGE_CLIENTS']} fallback={denied}>
              <ClientDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/clients/:clientId/history"
          element={
            <RequirePermission any={['VIEW_AUDIT']} fallback={denied}>
              <TransactionHistoryPage />
            </RequirePermission>
          }
        />
        <Route
          path="/pricing"
          element={
            <RequirePermission any={['MANAGE_PRICING']} fallback={denied}>
              <PricingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/revenue"
          element={
            <RequirePermission any={['VIEW_REVENUE']} fallback={denied}>
              <RevenuePage />
            </RequirePermission>
          }
        />
        <Route
          path="/incoming"
          element={
            <RequirePermission any={['MANAGE_INCOMING']} fallback={denied}>
              <IncomingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/operations/new"
          element={
            <RequirePermission any={['MANAGE_INCOMING']} fallback={denied}>
              <NewOperationPage />
            </RequirePermission>
          }
        />
        <Route
          path="/logs"
          element={
            <RequirePermission any={['VIEW_AUDIT']} fallback={denied}>
              <ConveraLogsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/operators"
          element={
            <RequirePermission any={OPERATOR_SCREEN_PERMISSIONS} fallback={denied}>
              <OperatorListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/operators/new"
          element={
            <RequirePermission any={[CREATE_OPERATOR]} fallback={denied}>
              <OperatorCreatePage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/operators/:username"
          element={
            <RequirePermission any={OPERATOR_SCREEN_PERMISSIONS} fallback={denied}>
              <OperatorDetailPage />
            </RequirePermission>
          }
        />
      </Route>

      {/* Catch-all — redirect to dashboard (ProtectedRoute will handle unauthed users) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
